// Environment configuration using edge function for secure database config
// This replaces environment variables with secure database configuration
import { debugLog } from "~/src/lib/debug";

type SupabaseConfig = {
  url: string;
  anonKey: string;
  serviceRoleKey?: string;
};

type PlatformConfig = {
  commissionRate: number;
  maxListingPrice: number;
  minListingPrice: number;
  defaultCurrency: string;
};

type AppConfig = {
  supportEmail: string;
  maintenanceMode: boolean;
  maxUploadSizeMB: number;
  checkoutDisabled?: boolean;
  captchaProvider?: string;
  turnstileSiteKey?: string;
};

export type AppConfiguration = {
  supabase: SupabaseConfig;
  platform: PlatformConfig;
  app: AppConfig;
  isDevelopment: boolean;
};

type RemoteConfigResponse = {
  supabase: SupabaseConfig;
  platform: PlatformConfig;
  app: AppConfig;
};

// Initialize the configuration service
let configCache: AppConfiguration | null = null;

export const getConfig = async () => {
  if (configCache) {
    debugLog('📋 Using cached config');
    return configCache;
  }

  debugLog('📋 Fetching config from edge function...');

  try {
    // Get configuration from edge function
    const projectId = import.meta.env?.VITE_SUPABASE_PROJECT_ID || 'ooezcyglpuebuqgjevmj';
    const baseUrl = import.meta.env?.VITE_SUPABASE_URL || `https://${projectId}.supabase.co`;
    const url = `${baseUrl}/functions/v1/get-config`;
    debugLog('📋 Fetching config from:', url);
    
    const fallbackConfig = getFallbackConfig();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (fallbackConfig.supabase.anonKey) {
      headers['apikey'] = fallbackConfig.supabase.anonKey;
      headers['Authorization'] = `Bearer ${fallbackConfig.supabase.anonKey}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    debugLog('📋 Config fetch response:', response.status, response.statusText);
    
    if (!response.ok) {
      debugLog('⚠️ Config fetch failed, attempting fallback', response.status, response.statusText);
      const fallback = buildFallbackResponse(fallbackConfig);
      if (fallback) {
        configCache = fallback;
        return configCache;
      }
      throw new Error(`Config fetch failed: ${response.status} ${response.statusText}`);
    }

    const config = (await response.json()) as RemoteConfigResponse;
    debugLog('✅ Loaded configuration from edge function');

    configCache = {
      ...config,
      isDevelopment: import.meta.env?.DEV === true,
    };

    return configCache;
  } catch (error) {
    console.error('❌ Failed to load configuration from edge function:', error);

    // Fallback to environment variables when available
    const fallback = buildFallbackResponse(getFallbackConfig());
    if (fallback) {
      console.warn('🔄 Using environment variables as fallback configuration');
      configCache = fallback;
      return configCache;
    }

    throw new Error('Configuration service unavailable and no fallback available');
  }
};

// Legacy export for backward compatibility
export const config = {
  get supabase() {
    throw new Error('Use getConfig() instead of config.supabase');
  },
  get platform() {
    throw new Error('Use getConfig() instead of config.platform');
  },
  get app() {
    throw new Error('Use getConfig() instead of config.app');
  },
  get isDevelopment() {
    return import.meta.env?.DEV === true;
  },
};

// Export a synchronous fallback for immediate use
export const getFallbackConfig = () => {
  const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL || 'https://ooezcyglpuebuqgjevmj.supabase.co';
  const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY || import.meta.env?.SUPABASE_ANON_KEY || '';

  if (!supabaseAnonKey) {
    console.error('❌ No Supabase anon key found in environment variables');
  }

  return {
    supabase: {
      url: supabaseUrl,
      anonKey: supabaseAnonKey,
    },
    platform: {
      commissionRate: parseFloat(import.meta.env?.PLATFORM_COMMISSION_RATE ?? "0.10"),
      maxListingPrice: 10000.00,
      minListingPrice: 0.01,
      defaultCurrency: "GBP",
    },
    app: {
      supportEmail: "support@control-exchange.com",
      maintenanceMode: false,
      maxUploadSizeMB: 10,
    },
    isDevelopment: import.meta.env?.DEV === true,
  };
};

type FallbackConfig = ReturnType<typeof getFallbackConfig>;

function hasValidSupabaseConfig(config: FallbackConfig) {
  return Boolean(config.supabase.url && config.supabase.anonKey);
}

function buildFallbackResponse(fallbackConfig: FallbackConfig): AppConfiguration | null {
  if (!hasValidSupabaseConfig(fallbackConfig)) {
    return null;
  }

  debugLog('📋 Using fallback configuration');

  return {
    supabase: fallbackConfig.supabase,
    platform: {
      commissionRate: parseFloat(import.meta.env?.PLATFORM_COMMISSION_RATE ?? "0.10"),
      maxListingPrice: 10000.00,
      minListingPrice: 0.01,
      defaultCurrency: "GBP",
    },
    app: {
      supportEmail: "support@control-exchange.com",
      maintenanceMode: false,
      maxUploadSizeMB: 10,
    },
    isDevelopment: import.meta.env?.DEV === true,
  };
}
